import i18n from 'config/i18n';
import * as yup from 'yup';

import { getLocale } from 'utils/date';
import {
  convertToNullWhenNaN,
  dateIsEqualOrIsAfterNextDay,
  isValidMaxDecimalPlaces
} from 'utils/validation';
import Decimal from 'decimal.js';

const t = i18n.getFixedT(getLocale(), 'common');
const tSurcharges = i18n.getFixedT(
  getLocale(),
  'common',
  'employerCostSettings.details.surcharges'
);

const fractionalNumbers = yup
  .number()
  .required(t('validation.valueIsRequired'))
  .transform(convertToNullWhenNaN)
  .test('isValidMaxDecimalPlaces', (value) => isValidMaxDecimalPlaces(value, 4))
  .nullable()
  .default(0);

const fractionalNumbersRequired = (label: string) =>
  yup
    .number()
    .required(t('validationFieldRequired', { name: label }))
    .transform(convertToNullWhenNaN)
    .test('isValidMaxDecimalPlaces', (value) =>
      isValidMaxDecimalPlaces(value, 4)
    )
    .nullable()
    .default(0);

const fractionalNumbersWithMinMaxRequired = (label: string) => {
  return yup
    .number()
    .required(t('validationFieldRequired', { name: label }))
    .transform(convertToNullWhenNaN)
    .min(0)
    .max(1)
    .test('isValidMaxDecimalPlaces', (value) =>
      isValidMaxDecimalPlaces(value, 4)
    )
    .nullable()
    .default(0);
};

const fractionalNumbersWithMin = yup
  .number()
  .transform(convertToNullWhenNaN)
  .min(0)
  .test('isValidMaxDecimalPlaces', (value) => isValidMaxDecimalPlaces(value, 4))
  .nullable()
  .default(0);

const decimalToPercentage = yup
  .string()
  .default('0 %')
  .trim()
  .transform((value: string) =>
    value.match(/[^\d\s.,%]/g)
      ? NaN
      : `${new Decimal(parseFloat(value) * 100).toDecimalPlaces(2)} %`.replace(
          '.',
          ','
        )
  )
  .matches(/^[\d]*([,.]{0,1}[\d]{1,})?\s?%?$/gi, {
    message: t('invalidPercentage')
  })
  .typeError(t('invalidPercentage'));

const fractionalNumbersWithMinRequired = (label: string) =>
  yup
    .number()
    .required(t('validationFieldRequired', { name: label }))
    .transform(convertToNullWhenNaN)
    .min(0)
    .test('isValidMaxDecimalPlaces', (value) =>
      isValidMaxDecimalPlaces(value, 4)
    )
    .nullable()
    .default(0);

export const schema = yup.object({
  employerId: yup
    .number()
    .typeError(
      t('validationFieldRequired', {
        name: t('employerCostSettings.details.general.employer_name_label')
      })
    )
    .required(
      t('validationFieldRequired', {
        name: t('employerCostSettings.details.general.employer_name_label')
      })
    )
    .default(null),
  nameBrief: yup
    .string()
    .required(
      t('validationFieldRequired', {
        name: t(
          'employerCostSettings.details.general.employer_name_short_label'
        )
      })
    )
    .default(''),
  claId: yup
    .number()
    .required(
      t('validationFieldRequired', {
        name: t('employerCostSettings.details.general.cla_label')
      })
    )
    .typeError(
      t('validationFieldRequired', {
        name: t('employerCostSettings.details.general.cla_label')
      })
    )
    .default(null),
  flags: yup.object({
    DEFAULT: yup.boolean().optional(),
    FIN_SAL_OVER_NPH: yup.boolean().optional().default(true),
    FIN_E_BAL_SEP: yup.boolean().optional(),
    COST_AGE_HOUR_CALC: yup.boolean().optional()
  }),
  valid: yup.object({
    from: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validFrom') }))
      .nullable()
      .typeError(t('validationDateInvalid', { name: t('validFrom') }))
      .default(null),
    to: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validTo') }))
      .nullable()
      .typeError(t('validationDateInvalid', { name: t('validTo') }))
      .default(null)
      .test(
        'isAfterStart',
        t('dateRangeMismatch', {
          startDate: t('validFrom'),
          endDate: t('validTo')
        }),
        dateIsEqualOrIsAfterNextDay
      )
  }),
  codeExt: yup.string().optional(),
  exportgroupCode: yup.string().optional(),
  invoiceGroup: yup.string().optional(),
  buhHandlingSvc: yup.string().optional(),
  FIN_SAL_OVER_NPH: yup.boolean().default(true),
  finEHolidaysPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('holidays_label')
  ),
  finEHolidaysBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('holidays_label')
  ),
  finESpecialleavePrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('special_leave_label')
  ),
  finESpecialleaveBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('special_leave_label')
  ),
  finESchoolingPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('education_label')
  ),
  finESchoolingBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('education_label')
  ),
  finETrainingPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('training_label')
  ),
  finETrainingBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('training_label')
  ),
  finEFestivedaysPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('festive_days_label')
  ),
  finEFestivedaysBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('festive_days_label')
  ),
  finEIllPrc: fractionalNumbersWithMinMaxRequired(tSurcharges('illness_label')),
  finEIllBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('illness_label')
  ),
  finEVariousownPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('various_own_label')
  ),
  finEVariousownBuhPrc: fractionalNumbersWithMinMaxRequired(
    tSurcharges('various_own_label')
  ),
  finEAgehoursPrc: decimalToPercentage,
  finEAgehoursBuhPrc: decimalToPercentage,
  totalNph: fractionalNumbersWithMin,
  totalNphBuh: fractionalNumbersWithMin,
  percentageUplift: yup.string().default('0 %'),
  percentageUpliftBuh: yup.string().default('0 %'),
  finEHolidaybonusPrc: fractionalNumbersWithMinRequired(
    tSurcharges('holiday_bonus_label')
  ),
  finEHolidaybonusBuhPrc: fractionalNumbersWithMinRequired(
    tSurcharges('holiday_bonus_label')
  ),
  finEYearendbonusPrc: fractionalNumbersWithMinRequired(
    tSurcharges('year_end_bonus_label')
  ),
  finEYearendbonusBuhPrc: fractionalNumbersWithMinRequired(
    tSurcharges('year_end_bonus_label')
  ),
  totalBonus: fractionalNumbersWithMin,
  totalBonusBuh: fractionalNumbersWithMin,
  finESalPrc: fractionalNumbersWithMinRequired(
    tSurcharges('k_employers_costs_label')
  ),
  finESalBuhPrc: fractionalNumbersWithMinRequired(
    tSurcharges('k_employers_costs_label')
  ),
  totalSalary: fractionalNumbersWithMin,
  totalSalaryBuh: fractionalNumbersWithMin,
  finSalNomHour: fractionalNumbersRequired(tSurcharges('hour_label')),
  finSalNomDuty: fractionalNumbersRequired(tSurcharges('duty_label')),
  finSalNomBuhHour: fractionalNumbersRequired(tSurcharges('buh_hour')),
  finSalNomBuh1Hour: fractionalNumbersRequired(tSurcharges('buh1_hour')),
  finSalNomBuh2Hour: fractionalNumbersRequired(tSurcharges('buh2_hour')),
  finSalNomBuh3Hour: fractionalNumbersRequired(tSurcharges('buh3_hour')),
  finSalNomBuh4Hour: fractionalNumbersRequired(tSurcharges('buh4_hour')),
  finSalNomBuh5Hour: fractionalNumbersRequired(tSurcharges('buh5_hour')),
  finSalNomBuh6Hour: fractionalNumbersRequired(tSurcharges('buh6_hour')),
  finEBalHrsPrc: fractionalNumbers,
  finEBalLvPrc: fractionalNumbers
});
